import { CorpSegmentNames } from 'src/constants/corp-segment-constants';
import { CorpSegmentsEntity, MasterCorpSegments, SegmentHierarchy } from 'src/models/AppContextModels';
import { ForecastTemplateMasterCorpSegmentDropdownValues } from 'src/models/ForecastModels';
import { getFileFromS3URI } from 'src/utils/aws-s3-services';
import { convertToDropdownValues } from './SegmentDropdownUtils';
import { filterDescendantsOfSelectedNodes, filterToLeafNodes } from './SegmentHierarchyUtils';

// Re-export everything so existing imports continue to work
export { convertToDropdownValues } from './SegmentDropdownUtils';
export { filterToLeafNodes } from './SegmentHierarchyUtils';
export { fetchCorpSegmentDataFromS3 } from './SegmentS3Service';
export { customBusinessSegmentSort, customCorpSegmentSort } from './SegmentSortUtils';

/** @deprecated Use filterDescendantsOfSelectedNodes from SegmentHierarchyUtils */
export const filterOutMasterCorpSegmentsFromSelectedValues = filterDescendantsOfSelectedNodes;

/**
 * Segments that should show all hierarchy nodes (not just leaves) in dropdowns.
 * Company shows all nodes because users select at any level of the company hierarchy.
 */
const SEGMENTS_SHOWING_ALL_NODES = new Set<string>([CorpSegmentNames.COMPANY]);

/**
 * Fetches BG-specific cost center hierarchy from S3, filters to descendants
 * of the BG's selected parent nodes, then returns only leaf nodes as dropdown values.
 */
export const getCostCentersForBusinessGroup = async (
  businessGroupCorpSegments: CorpSegmentsEntity[],
  masterCorpSegment: MasterCorpSegments
): Promise<ForecastTemplateMasterCorpSegmentDropdownValues> => {
  const costCenterSegment = businessGroupCorpSegments.find(
    (segment) => segment.corp_segment_name === CorpSegmentNames.COST_CENTER
  )!;

  const selectedParentNodes = (await getFileFromS3URI(costCenterSegment.corp_segment_values_s3_path)) as unknown as SegmentHierarchy[];

  const descendants = filterDescendantsOfSelectedNodes(
    masterCorpSegment.segment_hierarchy_data_from_s3,
    selectedParentNodes
  );

  return convertToDropdownValues(filterToLeafNodes(descendants), masterCorpSegment.segment_name);
};

/**
 * Fetches BG-specific product line hierarchy, reusing master data when the BG
 * points to the same S3 file. Returns only leaf nodes as dropdown values.
 */
export const getProductLinesForBusinessGroup = async (
  businessGroupCorpSegments: CorpSegmentsEntity[],
  masterCorpSegment: MasterCorpSegments
): Promise<ForecastTemplateMasterCorpSegmentDropdownValues> => {
  const productLineSegment = businessGroupCorpSegments.find(
    (segment) => segment.corp_segment_name === masterCorpSegment.segment_name
  )!;

  const isMasterFile = productLineSegment.corp_segment_values_s3_path === masterCorpSegment.segment_hierarchy_s3_path;
  const hierarchyData: SegmentHierarchy[] = isMasterFile
    ? masterCorpSegment.segment_hierarchy_data_from_s3
    : ((await getFileFromS3URI(productLineSegment.corp_segment_values_s3_path)) as unknown as SegmentHierarchy[]);

  return convertToDropdownValues(filterToLeafNodes(hierarchyData), masterCorpSegment.segment_name);
};

/**
 * Transforms all master corp segments into dropdown values for a business group.
 * Cost Center and Product Line have BG-specific S3 filtering.
 * All segments except Company show only leaf nodes.
 */
export const getTransformedCorpSegments = async (
  businessGroupCorpSegments: CorpSegmentsEntity[],
  masterCorpSegmentsData: MasterCorpSegments[]
): Promise<ForecastTemplateMasterCorpSegmentDropdownValues[]> => {
  const dropdownPromises = masterCorpSegmentsData.map(async (masterCorpSegment) => {
    const segmentName = masterCorpSegment.segment_name;

    if (segmentName === CorpSegmentNames.COST_CENTER) {
      return getCostCentersForBusinessGroup(businessGroupCorpSegments, masterCorpSegment);
    }

    if (segmentName === CorpSegmentNames.PRODUCT_LINE) {
      return getProductLinesForBusinessGroup(businessGroupCorpSegments, masterCorpSegment);
    }

    const hierarchyData = masterCorpSegment.segment_hierarchy_data_from_s3;
    const showAll = SEGMENTS_SHOWING_ALL_NODES.has(segmentName);
    const filteredData = showAll ? hierarchyData : filterToLeafNodes(hierarchyData);
    return convertToDropdownValues(filteredData, segmentName);
  });

  return Promise.all(dropdownPromises);
};
